import React, { useEffect, useMemo, useRef, useState, useCallback, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Link from '@atlaskit/link';
import { Text, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useUiModificationsEnabledStatus } from '@atlassian/ui-modifications-core/controllers';
import {
	useFormContext,
	useIsCollapsedAreaExpanded,
} from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { useMinimizableModal } from '@atlassian/jira-minimizable-modal/src/controllers/use-minimizable-modal/index.tsx';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useResizeObserver } from '@atlassian/jira-react-use-resize-observer/src/index.tsx';
import { CollapsibleAreaHeader } from './collapsible-area-header/index.tsx';
import messages from './messages.tsx';
import type { FieldsContainerProps } from './types.tsx';

export const SMART_FIELDS_IN_PRODUCT_HELP_ID = '3svLIYAFXrcqQlJXtqEc6G';

export const FieldsContainer = (props: FieldsContainerProps) => {
	const {
		fields: propFields,
		collapsedFields: propsCollapsedFields = [],
		optOutSmartFieldIds,
		FieldRenderer,
		tabIndex = 0, // TODO Remove "= 0" when cleaning migrate_gic_smart_fields_jira_labs
		...rest
	} = props;
	const [
		{
			ecosystem: {
				forge: { hasFinishedLoading },
			},
		},
	] = useFormContext();

	const uiModificationsStatus = useUiModificationsEnabledStatus(hasFinishedLoading);

	const { formatMessage } = useIntl();

	const { collapsedFields, fields } = useMemo(() => {
		let collapsedFieldsFound = uiModificationsStatus !== 'enabled' ? propsCollapsedFields : [];

		if (optOutSmartFieldIds && fg('opt_out_smart_fields_in_gic')) {
			collapsedFieldsFound = collapsedFieldsFound.filter(
				(field) => !optOutSmartFieldIds.includes(field),
			);
		}

		let fieldsFound: string[] = propFields.filter((field) => !collapsedFieldsFound.includes(field));

		if (!fieldsFound.length && collapsedFieldsFound.length) {
			fieldsFound = collapsedFieldsFound;
			collapsedFieldsFound = [];
		}

		return { collapsedFields: collapsedFieldsFound, fields: fieldsFound };
	}, [propFields, propsCollapsedFields, uiModificationsStatus, optOutSmartFieldIds]);

	const [isExpanded, { setExpandedCollapsedArea }] = useIsCollapsedAreaExpanded(tabIndex);

	// isCollapsed is only true once. When it's false, it should never get back to true
	const isCollapsed = useRef(!isExpanded);

	const { view, setView, setShouldFocusChanger } = useMinimizableModal();

	const openInProductHelp = useHelpPanelLink();

	const handleOnReadMoreClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>) => {
			event.preventDefault();
			if (view === 'modal') {
				setShouldFocusChanger(true);
				setView?.('mini');
			}
			openInProductHelp?.(SMART_FIELDS_IN_PRODUCT_HELP_ID);
		},
		[view, openInProductHelp, setShouldFocusChanger, setView],
	);

	// TODO Remove this condition when cleaning migrate_gic_smart_fields_jira_labs
	if (!FieldRenderer) return null;

	const handleOnClick = (_: unknown, analyticsEvent: UIAnalyticsEvent) => {
		isCollapsed.current = false;
		const isExpandedNewValue = !isExpanded;
		setExpandedCollapsedArea(tabIndex, isExpandedNewValue);
		fireUIAnalytics(analyticsEvent, 'moreFields', { isExpanded: isExpandedNewValue });
	};

	return (
		<Box xcss={fieldsContainerWrapperStyles}>
			<FieldRenderer {...rest} fields={fields} />
			{!!collapsedFields.length && (
				<CollapsibleWrapper>
					<CollapsibleAreaHeader
						totalFields={collapsedFields.length}
						isExpanded={isExpanded}
						onClick={handleOnClick}
					/>
					<CollapsibleArea isExpanded={isExpanded}>
						<Text as="p">
							{formatMessage(
								expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
									? messages.smartFieldsReadMoreIssueTermRefresh
									: messages.smartFieldsReadMore,
								{
									link: (chunks: ReactNode[]) => (
										<Link
											href="https://support.atlassian.com"
											appearance="default"
											onClick={handleOnReadMoreClick}
										>
											{chunks}
										</Link>
									),
								},
							)}
						</Text>
						{isCollapsed.current ? (
							<FieldRenderer {...rest} fields={collapsedFields} isCollapsed={isCollapsed.current} />
						) : (
							<LoadMoreFields {...rest} FieldRenderer={FieldRenderer} fields={collapsedFields} />
						)}
					</CollapsibleArea>
				</CollapsibleWrapper>
			)}
		</Box>
	);
};

/**
 * This component is loaded in order to replace the current FieldRenderer with collapsed fields, see the usage above
 * It ensures the current FieldRenderer is completely unmounted by using `useEffect` to load this component
 * Once this component is loaded, it re renders the FieldRenderer without the collapsed fields
 */
export const LoadMoreFields = ({ FieldRenderer, ...rest }: FieldsContainerProps) => {
	const [loaded, setLoaded] = useState(false);
	const [isFieldResponsive, setIsFieldResponsive] = useState(false);

	const collapsibleAreaRef = useRef<HTMLDivElement>(null);
	useResizeObserver({
		ref: collapsibleAreaRef,
		onResize() {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			setIsFieldResponsive(window.innerWidth < 560);
		},
	});

	useEffect(() => {
		setLoaded(true);
	}, []);

	if (!loaded) return null;
	if (!FieldRenderer) return null; // TODO Delete this condition when cleaning migrate_gic_smart_fields_jira_lab

	return (
		<div ref={collapsibleAreaRef}>
			<FieldRenderer {...rest} isFieldResponsive={isFieldResponsive} />
		</div>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CollapsibleWrapper = styled.div({
	borderWidth: '1px',
	borderStyle: 'solid',
	borderColor: `${token('color.border')}`,
	borderRadius: '4px',
	marginTop: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const CollapsibleArea = styled.div<{ isExpanded: boolean }>(({ isExpanded }) =>
	!isExpanded
		? {
				display: 'none',
			}
		: {
				// not using tokens here to match header alignment
				// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
				marginLeft: '28px',
				marginBottom: token('space.200'),
				padding: token('space.100'),
				paddingRight: token('space.200'),
				paddingTop: '0px',
			},
);

const fieldsContainerWrapperStyles = xcss({
	paddingBottom: 'space.200',
});
